//! Bridge that lets shared rich-text actions (toolbar formatting, the hyperlink
//! popup) target the formula bar when it is the active text editor, mirroring the
//! role `inlineEditorHandler` plays for the in-cell editor. The FormulaBar
//! registers imperative handlers on mount; everything delegates to the shared
//! `inlineEditorSpans` engine, which the bar has pointed at its own editor surface
//! while editing.

import type { CellEditorViewHandle } from '@/app/cellEditor/CellEditorView';
import { inlineEditorEvents } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorEvents';
import { inlineEditorSpans, type SpanFormatting } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorSpans';
import type { CellFormatSummary } from '@/app/quadratic-core-types';
import type { SheetPosTS } from '@/app/shared/types/size';

export interface FormulaBarRichTextHandlers {
  // True when the bar is editing a plain/rich-text cell (not a formula or code).
  isEditingText: () => boolean;
  getLocation: () => SheetPosTS | undefined;
  getView: () => CellEditorViewHandle | null;
  getFormatSummary: () => CellFormatSummary | undefined;
  // Recompute the highlight overlay from the live engine after a span change.
  refreshOverlay: () => void;
  focus: () => void;
}

class FormulaBarRichText {
  private handlers: FormulaBarRichTextHandlers | undefined;

  register(handlers: FormulaBarRichTextHandlers) {
    this.handlers = handlers;
  }

  unregister(handlers: FormulaBarRichTextHandlers) {
    if (this.handlers === handlers) this.handlers = undefined;
  }

  isEditingText(): boolean {
    return this.handlers?.isEditingText() ?? false;
  }

  get location(): SheetPosTS | undefined {
    return this.handlers?.getLocation();
  }

  focus() {
    this.handlers?.focus();
  }

  private hasSelection(): boolean {
    const view = this.handlers?.getView();
    if (!view) return false;
    const { start, end } = view.getSelection();
    return start !== end;
  }

  // Resolve the effective formatting at the cursor: span formatting wins, falling
  // back to the cell-level format summary. Used when creating empty spans so new
  // text inherits the surrounding state.
  private currentFormattingAll(): SpanFormatting {
    const summary = this.handlers?.getFormatSummary();
    const atCursor = inlineEditorSpans.getFormattingAtCursor();
    return {
      bold: atCursor?.bold ?? summary?.bold ?? false,
      italic: atCursor?.italic ?? summary?.italic ?? false,
      underline: atCursor?.underline ?? summary?.underline ?? false,
      strikeThrough: atCursor?.strikeThrough ?? summary?.strikeThrough ?? false,
      textColor: atCursor?.textColor ?? summary?.textColor ?? undefined,
    };
  }

  getFormattingStateForHyperlink(): SpanFormatting {
    return this.currentFormattingAll();
  }

  private toggle(property: 'bold' | 'italic' | 'underline' | 'strikeThrough'): boolean {
    if (!this.isEditingText()) return false;
    const summary = this.handlers?.getFormatSummary();
    if (this.hasSelection()) {
      inlineEditorSpans.toggleFormattingForSelection(property, summary?.[property] ?? undefined);
    } else {
      const current = this.currentFormattingAll();
      inlineEditorSpans.createEmptySpanAtCursor({ ...current, [property]: !current[property] });
    }
    this.handlers?.refreshOverlay();
    this.updateSelectionFormatting();
    return true;
  }

  toggleBold(): boolean {
    return this.toggle('bold');
  }

  toggleItalic(): boolean {
    return this.toggle('italic');
  }

  toggleUnderline(): boolean {
    return this.toggle('underline');
  }

  toggleStrikeThrough(): boolean {
    return this.toggle('strikeThrough');
  }

  setTextColor(color: string | undefined): boolean {
    if (!this.isEditingText()) return false;
    if (this.hasSelection()) {
      inlineEditorSpans.applyFormattingToSelection({ textColor: color });
    } else {
      const current = this.currentFormattingAll();
      inlineEditorSpans.createEmptySpanAtCursor({ ...current, textColor: color });
    }
    this.handlers?.refreshOverlay();
    this.updateSelectionFormatting();
    return true;
  }

  clearSpanFormatting(): boolean {
    if (!this.isEditingText()) return false;
    if (this.hasSelection()) {
      inlineEditorSpans.clearFormattingForSelection();
    } else if (!inlineEditorSpans.clearFormattingAtCursor()) {
      return false;
    }
    this.handlers?.refreshOverlay();
    this.updateSelectionFormatting();
    return true;
  }

  // Emit the selection formatting so the toolbar reflects the bar's caret/selection
  // state. Emits undefined when the bar is not the active text editor.
  updateSelectionFormatting() {
    if (!this.isEditingText()) {
      inlineEditorEvents.emit('selectionFormatting', undefined);
      return;
    }
    if (this.hasSelection()) {
      inlineEditorEvents.emit('selectionFormatting', inlineEditorSpans.getSelectionFormattingSummary());
      return;
    }
    const atCursor = inlineEditorSpans.getFormattingAtCursor();
    if (atCursor) {
      inlineEditorEvents.emit('selectionFormatting', atCursor);
      return;
    }
    const summary = this.handlers?.getFormatSummary();
    inlineEditorEvents.emit('selectionFormatting', {
      bold: summary?.bold ?? false,
      italic: summary?.italic ?? false,
      underline: summary?.underline ?? false,
      strikeThrough: summary?.strikeThrough ?? false,
      textColor: summary?.textColor ?? undefined,
    });
  }
}

export const formulaBarRichText = new FormulaBarRichText();
